import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  Tooltip,
  Pagination,
  Chip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Paper,
} from '@mui/material';
import {
  Search as SearchIcon,
  Folder as FolderIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Videocam as VideocamIcon,
  Image as ImageIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  DriveFileMove as MoveIcon,
  Close as CloseIcon,
  Autorenew as ReplaceIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { formatDate } from '../utils/dateUtils';
import LibraryPreviewModal from '../components/common/LibraryPreviewModal';

const ALLOWED_FILE_TYPES = '.jpg,.jpeg,.png,.webp,.mp4,.mov,.avi,.webm';

const emptyFolderForm = { name: '', description: '', downloadEnabled: true, coverImage: null };
const emptyUploadForm = { file: null, description: '', tags: '', downloadEnabled: '' }; // downloadEnabled: '' | 'true' | 'false'

const AdminDigitalLibrary = () => {
  const { getAuthHeaders, tokenManager } = useAuth();

  const [stats, setStats] = useState(null);
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(true);

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });

  const [search, setSearch] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState('all');
  const [sort, setSort] = useState('newest');

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);

  const [folderDialog, setFolderDialog] = useState({ open: false, mode: 'create', folder: null, form: emptyFolderForm, saving: false });
  const [uploadDialog, setUploadDialog] = useState({ open: false, form: emptyUploadForm, saving: false });
  const [replaceDialog, setReplaceDialog] = useState({ open: false, file: null, newFile: null, saving: false });
  const [moveDialog, setMoveDialog] = useState({ open: false, file: null, targetFolderId: '', saving: false });
  const [deleteFolderDialog, setDeleteFolderDialog] = useState({ open: false, folder: null, loading: false });
  const [deleteFileDialog, setDeleteFileDialog] = useState({ open: false, file: null, loading: false });

  const mediaSrc = (fileId) => `${API_URL}/digital-library/files/${fileId}/view?token=${encodeURIComponent(tokenManager.getToken())}`;
  const coverSrc = (folderId) => `${API_URL}/digital-library/folders/${folderId}/cover?token=${encodeURIComponent(tokenManager.getToken())}`;

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/digital-library/dashboard-stats`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (res.ok) setStats(data.stats);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFolders = useCallback(async () => {
    setLoadingFolders(true);
    try {
      const res = await fetch(`${API_URL}/digital-library/folders`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load folders');
      setFolders(data.folders || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load folders');
    } finally {
      setLoadingFolders(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchFolders();
    fetchStats();
  }, [fetchFolders, fetchStats]);

  const fetchFiles = useCallback(async (page = 1) => {
    if (!selectedFolder) return;
    setLoadingFiles(true);
    try {
      const params = new URLSearchParams({ page, limit: 24, search, sort });
      if (fileTypeFilter !== 'all') params.append('fileType', fileTypeFilter);

      const res = await fetch(`${API_URL}/digital-library/folders/${selectedFolder.folderId}/files?${params}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load files');
      setFiles(data.files || []);
      setPagination(data.pagination);
    } catch (error) {
      toast.error(error.message || 'Failed to load files');
    } finally {
      setLoadingFiles(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder, search, fileTypeFilter, sort]);

  useEffect(() => {
    if (selectedFolder) fetchFiles(1);
  }, [selectedFolder, search, fileTypeFilter, sort, fetchFiles]);

  const handleOpenFolder = (folder) => {
    setSelectedFolder(folder);
    setSearch('');
    setFileTypeFilter('all');
    setSort('newest');
  };

  const handleBack = () => {
    setSelectedFolder(null);
    setFiles([]);
  };

  const refreshCurrentFolder = async () => {
    await fetchFolders();
    if (selectedFolder) await fetchFiles(pagination.currentPage);
    await fetchStats();
  };

  // ---------------- Folder CRUD ----------------

  const openCreateFolder = () => {
    setFolderDialog({ open: true, mode: 'create', folder: null, form: emptyFolderForm, saving: false });
  };

  const openEditFolder = (folder) => {
    setFolderDialog({
      open: true,
      mode: 'edit',
      folder,
      form: { name: folder.name, description: folder.description, downloadEnabled: folder.downloadEnabled, coverImage: null },
      saving: false
    });
  };

  const handleFolderDialogClose = () => setFolderDialog((prev) => ({ ...prev, open: false }));

  const handleSaveFolder = async () => {
    const { mode, folder, form } = folderDialog;
    if (!form.name.trim()) {
      toast.error('Folder name is required');
      return;
    }
    setFolderDialog((prev) => ({ ...prev, saving: true }));
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('description', form.description || '');
      fd.append('downloadEnabled', form.downloadEnabled);
      if (form.coverImage) fd.append('coverImage', form.coverImage);

      const url = mode === 'create' ? `${API_URL}/digital-library/folders` : `${API_URL}/digital-library/folders/${folder.folderId}`;
      const method = mode === 'create' ? 'POST' : 'PUT';

      const res = await fetch(url, { method, headers: { Authorization: `Bearer ${tokenManager.getToken()}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save folder');

      toast.success(mode === 'create' ? 'Folder created successfully' : 'Folder updated successfully');
      setFolderDialog((prev) => ({ ...prev, open: false, saving: false }));
      await fetchFolders();
    } catch (error) {
      toast.error(error.message || 'Failed to save folder');
      setFolderDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDeleteFolderConfirm = async () => {
    const folder = deleteFolderDialog.folder;
    setDeleteFolderDialog((prev) => ({ ...prev, loading: true }));
    try {
      const res = await fetch(`${API_URL}/digital-library/folders/${folder.folderId}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete folder');
      toast.success('Folder deleted successfully');
      setDeleteFolderDialog({ open: false, folder: null, loading: false });
      if (selectedFolder?.folderId === folder.folderId) handleBack();
      await fetchFolders();
      await fetchStats();
    } catch (error) {
      toast.error(error.message || 'Failed to delete folder');
      setDeleteFolderDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  // ---------------- File upload / manage ----------------

  const openUploadDialog = () => setUploadDialog({ open: true, form: emptyUploadForm, saving: false });
  const closeUploadDialog = () => setUploadDialog((prev) => ({ ...prev, open: false }));

  const handleUpload = async () => {
    const { form } = uploadDialog;
    if (!form.file) {
      toast.error('Please select a file to upload');
      return;
    }
    setUploadDialog((prev) => ({ ...prev, saving: true }));
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('folder', selectedFolder.folderId);
      fd.append('description', form.description || '');
      fd.append('tags', form.tags || '');
      fd.append('downloadEnabled', form.downloadEnabled);

      const res = await fetch(`${API_URL}/digital-library/files/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${tokenManager.getToken()}` }, body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to upload file');

      toast.success('File uploaded successfully');
      setUploadDialog({ open: false, form: emptyUploadForm, saving: false });
      await refreshCurrentFolder();
    } catch (error) {
      toast.error(error.message || 'Failed to upload file');
      setUploadDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const openReplaceDialog = (file) => setReplaceDialog({ open: true, file, newFile: null, saving: false });
  const closeReplaceDialog = () => setReplaceDialog({ open: false, file: null, newFile: null, saving: false });

  const handleReplace = async () => {
    if (!replaceDialog.newFile) {
      toast.error('Please select a replacement file');
      return;
    }
    setReplaceDialog((prev) => ({ ...prev, saving: true }));
    try {
      const fd = new FormData();
      fd.append('file', replaceDialog.newFile);
      const res = await fetch(`${API_URL}/digital-library/files/${replaceDialog.file.fileId}/replace`, {
        method: 'PUT', headers: { Authorization: `Bearer ${tokenManager.getToken()}` }, body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to replace file');
      toast.success('File replaced successfully');
      closeReplaceDialog();
      await refreshCurrentFolder();
    } catch (error) {
      toast.error(error.message || 'Failed to replace file');
      setReplaceDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const openMoveDialog = (file) => setMoveDialog({ open: true, file, targetFolderId: '', saving: false });
  const closeMoveDialog = () => setMoveDialog({ open: false, file: null, targetFolderId: '', saving: false });

  const handleMove = async () => {
    if (!moveDialog.targetFolderId) {
      toast.error('Please select a destination folder');
      return;
    }
    setMoveDialog((prev) => ({ ...prev, saving: true }));
    try {
      const res = await fetch(`${API_URL}/digital-library/files/${moveDialog.file.fileId}/move`, {
        method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({ folderId: moveDialog.targetFolderId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to move file');
      toast.success('File moved successfully');
      closeMoveDialog();
      await refreshCurrentFolder();
    } catch (error) {
      toast.error(error.message || 'Failed to move file');
      setMoveDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDeleteFileConfirm = async () => {
    const file = deleteFileDialog.file;
    setDeleteFileDialog((prev) => ({ ...prev, loading: true }));
    try {
      const res = await fetch(`${API_URL}/digital-library/files/${file.fileId}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete file');
      toast.success('File deleted successfully');
      setDeleteFileDialog({ open: false, file: null, loading: false });
      await refreshCurrentFolder();
    } catch (error) {
      toast.error(error.message || 'Failed to delete file');
      setDeleteFileDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handlePreview = (idx) => {
    setPreviewIndex(idx);
    setPreviewOpen(true);
  };

  const handleDownload = async (file) => {
    try {
      const response = await fetch(`${API_URL}/digital-library/files/${file.fileId}/download`, { headers: getAuthHeaders() });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Download failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.originalName || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error(error.message || 'Failed to download file');
    }
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', p: 3 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '30px' }}>Digital Library Management</Typography>
      <Typography variant="h6" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 3 }}>
        Manage folders and media shared with employees
      </Typography>

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Folders', value: stats.totalFolders, icon: <FolderIcon /> },
            { label: 'Images', value: stats.totalImages, icon: <ImageIcon /> },
            { label: 'Videos', value: stats.totalVideos, icon: <VideocamIcon /> },
            { label: 'Storage Used', value: stats.totalStorageFormatted, icon: <StorageIcon /> },
          ].map((s) => (
            <Grid item xs={6} md={3} key={s.label}>
              <Paper sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ color: 'primary.main' }}>{s.icon}</Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {!selectedFolder ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateFolder}>New Folder</Button>
          </Box>

          {loadingFolders ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : folders.length === 0 ? (
            <Alert severity="info">No folders yet. Create your first folder to start uploading media.</Alert>
          ) : (
            <Grid container spacing={3}>
              {folders.map((folder) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={folder.folderId}>
                  <Card sx={{ borderRadius: 2, height: '100%', position: 'relative' }}>
                    <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1, display: 'flex' }}>
                      <Tooltip title="Rename / Edit">
                        <IconButton size="small" sx={{ bgcolor: 'rgba(255,255,255,0.85)' }} onClick={() => openEditFolder(folder)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" sx={{ bgcolor: 'rgba(255,255,255,0.85)', ml: 0.5 }} onClick={() => setDeleteFolderDialog({ open: true, folder, loading: false })}>
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <CardActionArea onClick={() => handleOpenFolder(folder)}>
                      {folder.coverImageUrl ? (
                        <CardMedia component="img" height="140" image={coverSrc(folder.folderId)} alt={folder.name} />
                      ) : (
                        <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.light' }}>
                          <FolderIcon sx={{ fontSize: 56, color: 'primary.main' }} />
                        </Box>
                      )}
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>{folder.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {folder.fileCount} {folder.fileCount === 1 ? 'file' : 'files'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          By {folder.createdByName || 'Unknown'} • {formatDate(folder.createdAt)}
                        </Typography>
                        {!folder.downloadEnabled && (
                          <Chip size="small" label="Downloads disabled" color="warning" sx={{ mt: 1 }} />
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handleBack}><ArrowBackIcon /></IconButton>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{selectedFolder.name}</Typography>
            </Box>
            <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={openUploadDialog}>Upload File</Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search files, tags, description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select value={fileTypeFilter} onChange={(e) => setFileTypeFilter(e.target.value)}>
                <MenuItem value="all">All types</MenuItem>
                <MenuItem value="image">Images</MenuItem>
                <MenuItem value="video">Videos</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select value={sort} onChange={(e) => setSort(e.target.value)}>
                <MenuItem value="newest">Newest first</MenuItem>
                <MenuItem value="oldest">Oldest first</MenuItem>
                <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                <MenuItem value="size-desc">Largest first</MenuItem>
                <MenuItem value="size-asc">Smallest first</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loadingFiles ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : files.length === 0 ? (
            <Alert severity="info">No files in this folder yet. Click "Upload File" to add some.</Alert>
          ) : (
            <>
              <Grid container spacing={2}>
                {files.map((file, idx) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={file.fileId}>
                    <Card sx={{ borderRadius: 2, position: 'relative', '&:hover .library-hover-actions': { opacity: 1 } }}>
                      <Box sx={{ position: 'relative', height: 160, bgcolor: 'grey.200' }}>
                        {file.fileType === 'image' ? (
                          <Box component="img" src={mediaSrc(file.fileId)} alt={file.originalName}
                               sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <VideocamIcon sx={{ fontSize: 48, color: 'grey.500' }} />
                          </Box>
                        )}
                        <Chip
                          size="small"
                          icon={file.fileType === 'video' ? <VideocamIcon /> : <ImageIcon />}
                          label={file.fileType}
                          sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff' }}
                        />
                        <Box
                          className="library-hover-actions"
                          sx={{
                            position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap',
                            opacity: 0, transition: 'opacity 0.2s'
                          }}
                        >
                          <Tooltip title="Preview">
                            <IconButton size="small" sx={{ color: '#fff' }} onClick={() => handlePreview(idx)}><VisibilityIcon /></IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton size="small" sx={{ color: '#fff' }} onClick={() => handleDownload(file)}><DownloadIcon /></IconButton>
                          </Tooltip>
                          <Tooltip title="Replace">
                            <IconButton size="small" sx={{ color: '#fff' }} onClick={() => openReplaceDialog(file)}><ReplaceIcon /></IconButton>
                          </Tooltip>
                          <Tooltip title="Move">
                            <IconButton size="small" sx={{ color: '#fff' }} onClick={() => openMoveDialog(file)}><MoveIcon /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" sx={{ color: '#fff' }} onClick={() => setDeleteFileDialog({ open: true, file, loading: false })}><DeleteIcon /></IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{file.originalName}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatDate(file.createdAt)} • {file.fileSizeFormatted}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">By {file.uploadedByName}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination count={pagination.totalPages} page={pagination.currentPage} onChange={(e, page) => fetchFiles(page)} color="primary" />
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* ---------------- Create / Edit Folder Dialog ---------------- */}
      <Dialog open={folderDialog.open} onClose={handleFolderDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {folderDialog.mode === 'create' ? 'Create Folder' : 'Edit Folder'}
          <IconButton onClick={handleFolderDialogClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Folder Name" fullWidth required margin="normal"
            value={folderDialog.form.name}
            onChange={(e) => setFolderDialog((prev) => ({ ...prev, form: { ...prev.form, name: e.target.value } }))}
          />
          <TextField
            label="Description" fullWidth multiline rows={2} margin="normal"
            value={folderDialog.form.description}
            onChange={(e) => setFolderDialog((prev) => ({ ...prev, form: { ...prev.form, description: e.target.value } }))}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={folderDialog.form.downloadEnabled}
                onChange={(e) => setFolderDialog((prev) => ({ ...prev, form: { ...prev.form, downloadEnabled: e.target.checked } }))}
              />
            }
            label="Allow employees to download files in this folder by default"
          />
          <Paper
            variant="outlined"
            sx={{ p: 2, mt: 2, border: '2px dashed', borderColor: 'grey.300', textAlign: 'center', cursor: 'pointer' }}
            onClick={() => document.getElementById('folder-cover-input').click()}
          >
            <input
              id="folder-cover-input" type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => setFolderDialog((prev) => ({ ...prev, form: { ...prev.form, coverImage: e.target.files?.[0] || null } }))}
            />
            <Typography variant="body2">
              {folderDialog.form.coverImage ? folderDialog.form.coverImage.name : 'Click to select an optional cover image'}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFolderDialogClose} disabled={folderDialog.saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveFolder} disabled={folderDialog.saving}
                  startIcon={folderDialog.saving ? <CircularProgress size={16} /> : null}>
            {folderDialog.saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Upload File Dialog ---------------- */}
      <Dialog open={uploadDialog.open} onClose={closeUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload File to "{selectedFolder?.name}"
          <IconButton onClick={closeUploadDialog} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Paper
            variant="outlined"
            sx={{
              p: 3, border: '2px dashed', borderColor: uploadDialog.form.file ? 'success.main' : 'grey.300',
              backgroundColor: uploadDialog.form.file ? 'success.50' : 'grey.50', textAlign: 'center', cursor: 'pointer'
            }}
            onClick={() => document.getElementById('library-file-input').click()}
          >
            <input
              id="library-file-input" type="file" accept={ALLOWED_FILE_TYPES} style={{ display: 'none' }}
              onChange={(e) => setUploadDialog((prev) => ({ ...prev, form: { ...prev.form, file: e.target.files?.[0] || null } }))}
            />
            <CloudUploadIcon sx={{ fontSize: 48, color: uploadDialog.form.file ? 'success.main' : 'grey.500', mb: 1 }} />
            <Typography variant="body1">{uploadDialog.form.file ? uploadDialog.form.file.name : 'Click to select an image or video'}</Typography>
            <Typography variant="body2" color="text.secondary">JPG, PNG, WEBP, MP4, MOV, AVI, WEBM</Typography>
          </Paper>
          <TextField
            label="Description (optional)" fullWidth margin="normal"
            value={uploadDialog.form.description}
            onChange={(e) => setUploadDialog((prev) => ({ ...prev, form: { ...prev.form, description: e.target.value } }))}
          />
          <TextField
            label="Tags (comma separated, optional)" fullWidth margin="normal"
            value={uploadDialog.form.tags}
            onChange={(e) => setUploadDialog((prev) => ({ ...prev, form: { ...prev.form, tags: e.target.value } }))}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Download permission</InputLabel>
            <Select
              label="Download permission"
              value={uploadDialog.form.downloadEnabled}
              onChange={(e) => setUploadDialog((prev) => ({ ...prev, form: { ...prev.form, downloadEnabled: e.target.value } }))}
            >
              <MenuItem value="">Inherit from folder ({selectedFolder?.downloadEnabled ? 'enabled' : 'disabled'})</MenuItem>
              <MenuItem value="true">Enabled</MenuItem>
              <MenuItem value="false">Disabled</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUploadDialog} disabled={uploadDialog.saving}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={uploadDialog.saving}
                  startIcon={uploadDialog.saving ? <CircularProgress size={16} /> : null}>
            {uploadDialog.saving ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Replace File Dialog ---------------- */}
      <Dialog open={replaceDialog.open} onClose={closeReplaceDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Replace "{replaceDialog.file?.originalName}"
          <IconButton onClick={closeReplaceDialog} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Paper
            variant="outlined"
            sx={{ p: 3, border: '2px dashed', borderColor: 'grey.300', textAlign: 'center', cursor: 'pointer' }}
            onClick={() => document.getElementById('library-replace-input').click()}
          >
            <input
              id="library-replace-input" type="file" accept={ALLOWED_FILE_TYPES} style={{ display: 'none' }}
              onChange={(e) => setReplaceDialog((prev) => ({ ...prev, newFile: e.target.files?.[0] || null }))}
            />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
            <Typography variant="body1">{replaceDialog.newFile ? replaceDialog.newFile.name : 'Click to select the replacement file'}</Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReplaceDialog} disabled={replaceDialog.saving}>Cancel</Button>
          <Button variant="contained" onClick={handleReplace} disabled={replaceDialog.saving}
                  startIcon={replaceDialog.saving ? <CircularProgress size={16} /> : null}>
            {replaceDialog.saving ? 'Replacing...' : 'Replace'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Move File Dialog ---------------- */}
      <Dialog open={moveDialog.open} onClose={closeMoveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Move "{moveDialog.file?.originalName}"
          <IconButton onClick={closeMoveDialog} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Destination folder</InputLabel>
            <Select
              label="Destination folder"
              value={moveDialog.targetFolderId}
              onChange={(e) => setMoveDialog((prev) => ({ ...prev, targetFolderId: e.target.value }))}
            >
              {folders.filter((f) => f.folderId !== selectedFolder?.folderId).map((f) => (
                <MenuItem key={f.folderId} value={f.folderId}>{f.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMoveDialog} disabled={moveDialog.saving}>Cancel</Button>
          <Button variant="contained" onClick={handleMove} disabled={moveDialog.saving}
                  startIcon={moveDialog.saving ? <CircularProgress size={16} /> : null}>
            {moveDialog.saving ? 'Moving...' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Delete Folder Confirmation ---------------- */}
      <Dialog open={deleteFolderDialog.open} onClose={() => setDeleteFolderDialog({ open: false, folder: null, loading: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete Folder</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the folder "{deleteFolderDialog.folder?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will permanently delete all {deleteFolderDialog.folder?.fileCount || 0} file(s) inside it. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFolderDialog({ open: false, folder: null, loading: false })} disabled={deleteFolderDialog.loading}>Cancel</Button>
          <Button onClick={handleDeleteFolderConfirm} color="error" variant="contained" disabled={deleteFolderDialog.loading}
                  startIcon={deleteFolderDialog.loading ? <CircularProgress size={16} /> : null}>
            {deleteFolderDialog.loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Delete File Confirmation ---------------- */}
      <Dialog open={deleteFileDialog.open} onClose={() => setDeleteFileDialog({ open: false, file: null, loading: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete File</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteFileDialog.file?.originalName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFileDialog({ open: false, file: null, loading: false })} disabled={deleteFileDialog.loading}>Cancel</Button>
          <Button onClick={handleDeleteFileConfirm} color="error" variant="contained" disabled={deleteFileDialog.loading}
                  startIcon={deleteFileDialog.loading ? <CircularProgress size={16} /> : null}>
            {deleteFileDialog.loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <LibraryPreviewModal
        open={previewOpen}
        files={files}
        index={previewIndex}
        onClose={() => setPreviewOpen(false)}
        onNavigate={(newIndex) => { if (newIndex >= 0 && newIndex < files.length) setPreviewIndex(newIndex); }}
        onDownload={handleDownload}
      />
    </Box>
  );
};

export default AdminDigitalLibrary;
